import { useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { requestCode, verifyCode } from "./api";
import { useAuthStore } from "@/store/authStore";
import { Input } from "@/shared/ui/Input";

// +998 phone mask: keep only the 9 national digits, render grouped.
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^998/, "").slice(0, 9);
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 5),
    digits.slice(5, 7),
    digits.slice(7, 9),
  ].filter(Boolean);
  return parts.join(" ");
}

// E.164 for the backend: +998XXXXXXXXX.
function toE164(masked: string): string {
  return "+998" + masked.replace(/\D/g, "");
}

const CODE_LENGTH = 4;

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);

  const phoneDigits = phone.replace(/\D/g, "");
  const codeStr = code.join("");

  async function onSendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (phoneDigits.length !== 9) {
      setError(t("auth.invalidPhone"));
      return;
    }
    setBusy(true);
    try {
      await requestCode(toE164(phone));
      setStep("code");
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
    } catch {
      setError(t("auth.genericError"));
    } finally {
      setBusy(false);
    }
  }

  function onCodeChange(idx: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    setError(null);
    setCode((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < CODE_LENGTH - 1) codeRefs.current[idx + 1]?.focus();
  }

  function onCodeKeyDown(idx: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (codeStr.length !== CODE_LENGTH) {
      setError(t("auth.invalidCode"));
      return;
    }
    setBusy(true);
    try {
      const tokens = await verifyCode(toE164(phone), codeStr);
      // Store tokens + verify support rights (/support/me/). 403 -> no permission.
      await login(tokens);
      navigate("/customers", { replace: true });
    } catch (err) {
      const status = err instanceof AxiosError ? err.response?.status : undefined;
      if (status === 403) setError(t("auth.noPermission"));
      else if (status === 400 || status === 401) setError(t("auth.wrongCode"));
      else setError(t("auth.genericError"));
      // Clear OTP so the operator can retry cleanly.
      setCode(Array(CODE_LENGTH).fill(""));
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50 p-6">
      <div className="w-full max-w-[420px]">
        <div className="mb-7 flex items-center gap-2.5">
          <img src="/assets/nasiyah-wordmark.svg" alt="Nasiyah" className="h-7 w-auto" />
          <span className="rounded-md bg-grey-100 px-[7px] py-[3px] text-[10.5px] font-bold uppercase tracking-wider text-grey-500">
            {t("app.support")}
          </span>
        </div>

        <div className="rounded-card border border-grey-200 bg-white p-7 shadow-card">
          <h1 className="text-xl font-bold tracking-[-0.3px] text-grey-900">
            {t("auth.title")}
          </h1>
          <p className="mt-1 text-[13px] text-grey-500">{t("auth.subtitle")}</p>

          {step === "phone" ? (
            <form onSubmit={onSendCode} className="mt-6">
              <label className="mb-1.5 block text-[13px] font-semibold text-grey-700">
                {t("auth.phoneLabel")}
              </label>
              {/* Composite control: outer div owns the border/focus ring; inner Input is borderless. */}
              <div className="flex h-[42px] items-center overflow-hidden rounded-control border border-grey-300 bg-white focus-within:border-green focus-within:shadow-focus">
                <span className="select-none pl-3.5 pr-2 text-[14px] font-semibold text-grey-500">
                  +998
                </span>
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="90 123 45 67"
                  className="tnum h-full border-0 bg-transparent pr-3.5 text-[14px] focus-visible:ring-0"
                />
              </div>

              {error && <p className="mt-3 text-[13px] font-medium text-red">{error}</p>}

              <button
                type="submit"
                disabled={busy}
                className="mt-5 flex h-10 w-full items-center justify-center rounded-control bg-green text-[14px] font-semibold text-white transition-colors hover:bg-green-dark disabled:opacity-60"
              >
                {busy ? t("auth.sending") : t("auth.sendCode")}
              </button>
            </form>
          ) : (
            <form onSubmit={onVerify} className="mt-6">
              <label className="mb-1.5 block text-[13px] font-semibold text-grey-700">
                {t("auth.codeLabel")}
              </label>
              <p className="mb-3 text-[12px] text-grey-400">{t("auth.codeHint")}</p>
              <div className="flex gap-3">
                {code.map((digit, idx) => (
                  <Input
                    key={idx}
                    ref={(el) => (codeRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => onCodeChange(idx, e.target.value)}
                    onKeyDown={(e) => onCodeKeyDown(idx, e)}
                    className="tnum h-14 text-center text-[22px] font-bold text-grey-900"
                  />
                ))}
              </div>

              {error && <p className="mt-3 text-[13px] font-medium text-red">{error}</p>}

              <button
                type="submit"
                disabled={busy}
                className="mt-5 flex h-10 w-full items-center justify-center rounded-control bg-green text-[14px] font-semibold text-white transition-colors hover:bg-green-dark disabled:opacity-60"
              >
                {busy ? t("auth.verifying") : t("auth.enter")}
              </button>

              <div className="mt-4 flex items-center justify-between text-[13px]">
                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setCode(Array(CODE_LENGTH).fill(""));
                    setError(null);
                  }}
                  className="font-semibold text-grey-600 hover:text-grey-900"
                >
                  {t("auth.back")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => requestCode(toE164(phone))}
                  className="font-semibold text-green-dark hover:underline disabled:opacity-60"
                >
                  {t("auth.resend")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
